import { AiScanningLogo } from 'components/Custom/AiScanningLogo';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Col, Row, Tooltip } from 'antd';
import { EntityButton } from '@monorepo/shared-lib';
import { ReactComponent as LogoCin7 } from 'assets/images/icons/entityButtonCin7.svg';
import LogoCin7Dark from 'assets/images/icons/entityButtonCin7-dark.png';
import { ReactComponent as LogoExcel } from 'assets/images/icons/entityButtonExcel.svg';
import { ReactComponent as LogoQuickbooks } from 'assets/images/icons/entityButtonQuickbooks.svg';
import { ReactComponent as LogoXero } from 'assets/images/icons/entityButtonXero.svg';
import { ReactComponent as LogoZoho } from 'assets/images/icons/entityButtonZoho.svg';
import LogoZohoDark from 'assets/images/icons/entityButtonZoho-dark.png';
import { ReactComponent as IconBetaStar } from 'assets/images/icons/iconBetaStar.svg';
import LogoQuickbooksButton from 'assets/images/quickbooks_button.png';
import { FeatureDotBadge } from 'components/Custom/FeatureDotBadge';
import { ConnectCin7 } from 'components/Entity/connect-cin7';
import { EditFriendlyName } from 'components/OrganisationDetails/edit-friendly-name';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import routes from 'routes';
import { Provider } from 'store/api/enums';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import {
  addOrganisationRequest,
  fetchAddUrlRequest,
} from 'store/organisation/actions';
import {
  EditOrganisationParams,
  NewOrganisationParams,
} from 'store/organisation/models';
import { isZohoEnabled } from 'utils/feature-flag-utils';

export const ConnectEntity: React.FC = () => {
  const loggedUser = useSelector(getAuthUser);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const shouldApplyHover = loggedUser?.email?.includes('@sharklasers.com');

  const onConnectCin7 = () => {
    const drawerData: DrawerData = {
      title: t('organisation.connect-cin7-title'),
      type: DrawerType.CONNECT_CIN7,
      component: <ConnectCin7 />,
    };
    dispatch(turnDrawerOn(drawerData));
  };
  const onConnectExcel = (providerName: Provider) => {
    const getDetails = (orgValues: EditOrganisationParams) => {
      createNewEntity(orgValues, providerName);
    };
    const isReceipts = providerName === Provider.RECEIPTS;
    const drawerData: DrawerData = {
      title: t('organisation.add-new-entity'),
      subtitle: isReceipts ? (
        <div style={{ marginTop: 10 }}>
          {t('organisation.connect-entity-receipts-subtitle')}{' '}
          <Tooltip title={t('organisation.receipts-beta-tooltip')}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--color-primary, #41aaaf)',
                backgroundColor: 'var(--color-layout-bg, #E9F6F7)',
                padding: '2px 6px',
                borderRadius: 4,
                border: '1px solid var(--color-border, #dbeae3)',
                cursor: 'help',
              }}
            >
              <IconBetaStar style={{ width: 14, height: 14, flexShrink: 0 }} />
              {t('common.beta')}
            </span>
          </Tooltip>
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          {t('organisation.enter-details-below')}
        </div>
      ),
      type: DrawerType.EDIT_FRIENDLY_NAME,
      wide: true,
      component: (
        <EditFriendlyName
          friendlyName=""
          organisationId=""
          onClose={() => dispatch(turnDrawerOff())}
          editOrganisationName={getDetails}
          isAddMode={true}
          addModeProvider={providerName}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };
  const createNewEntity = (
    orgValues: EditOrganisationParams,
    providerName: Provider
  ) => {
    // Map EditOrganisationParams to NewOrganisationParams
    const newOrgParams: NewOrganisationParams = {
      providerName,
      legalName: orgValues.entityName || '',
      name: orgValues.newName || '',
      currency: orgValues.currency || '',
      countryCode: orgValues.country || '',
      scope: providerName,
      taxNumber: orgValues.taxNumber || '',
      alternateName: orgValues.alternateName || '',
      code: orgValues.code || '',
      contactPersonName: orgValues.contactPersonName || '',
      contactPersonEmail: orgValues.contactPersonEmail || '',
      contactPersonTelephone: orgValues.contactPersonTelephone || '',
      registeredAddress: orgValues.registeredAddress || '',
      notes: orgValues.notes || '',
    };

    dispatch(addOrganisationRequest(newOrgParams));
    dispatch(turnDrawerOff());
  };
  const connectOrganisation = (providerName: Provider) => {
    const sourceUrl = location.pathname ? location.pathname : routes.main;
    if (loggedUser) {
      const providerFormattedName = t(`common.${providerName?.toLowerCase()}`, {
        defaultValue: providerName,
      });
      dispatch(
        fetchAddUrlRequest(
          providerName,
          loggedUser.userUuid,
          sourceUrl,
          providerFormattedName
        )
      );
      // clearing cache
      localStorage.removeItem(`gen-tax-tax-rates-${loggedUser?.userUuid}`);
      localStorage.removeItem(`vies-infos-${loggedUser?.userUuid}`);

      dispatch(turnDrawerOff());
    }
  };
  return (
    <div className="connect-entity">
      <Row
        justify={'space-between'}
        gutter={[0, 15]}
        style={{ marginTop: '20px' }}
      >
        <Col>
          <EntityButton
            type="button"
            aria-label={t('organisation.connect-with-provider', {
              provider: t('common.xero'),
            })}
            onClick={() => connectOrganisation(Provider.XERO)}
          >
            <LogoXero style={{ width: 75 }} />
          </EntityButton>
        </Col>
        <Col>
          <EntityButton
            type="button"
            aria-label={t('organisation.connect-with-provider', {
              provider: t('common.quickbooks'),
            })}
            onClick={() => connectOrganisation(Provider.QUICKBOOKS)}
            $padding="35px"
            onMouseEnter={
              shouldApplyHover ? () => setIsHovered(true) : undefined
            }
            onMouseLeave={
              shouldApplyHover ? () => setIsHovered(false) : undefined
            }
          >
            {!isHovered || !shouldApplyHover ? (
              <LogoQuickbooks />
            ) : (
              <img src={LogoQuickbooksButton} style={{ maxWidth: '125px' }} />
            )}
          </EntityButton>
        </Col>
      </Row>

      <Row
        justify={'space-between'}
        gutter={[0, 15]}
        style={{ marginTop: '20px' }}
      >
        <Col>
          <EntityButton
            type="button"
            aria-label={t('organisation.connect-with-provider', {
              provider: t('common.cin7'),
            })}
            onClick={() => onConnectCin7()}
          >
            <span className="connect-entity-logo">
              <LogoCin7
                className="connect-entity-logo__light"
                style={{ width: 110 }}
                aria-hidden
              />
              <img
                className="connect-entity-logo__dark"
                src={LogoCin7Dark}
                alt=""
                style={{ width: 110, height: 'auto' }}
              />
            </span>
          </EntityButton>
          <span
            style={{
              position: 'absolute',
              bottom: 20,
              left: 23,
              fontSize: 10,
              color: 'var(--color-on-logo-tile-muted, #5a6a6a)',
              padding: '2px 4px',
              fontStyle: 'italic',
            }}
          >
            {t('organisation.vat-checker-only')}
          </span>
        </Col>
        {isZohoEnabled() ? (
          <Col>
            <EntityButton
              type="button"
              aria-label={t('organisation.connect-with-provider', {
                provider: t('common.zoho'),
              })}
              onClick={() => connectOrganisation(Provider.ZOHO)}
            >
              <span className="connect-entity-logo">
                <LogoZoho
                  className="connect-entity-logo__light"
                  style={{ width: 110 }}
                  aria-hidden
                />
                <img
                  className="connect-entity-logo__dark"
                  src={LogoZohoDark}
                  alt=""
                  style={{ width: 165, height: 'auto' }}
                />
              </span>
            </EntityButton>
          </Col>
        ) : (
          <Col>
            <EntityButton
              type="button"
              comingSoon={true}
              disabled
              aria-label={t('organisation.connect-provider-coming-soon', {
                provider: t('common.zoho'),
              })}
              onClick={() => {}}
            >
              <span className="connect-entity-logo">
                <LogoZoho
                  className="connect-entity-logo__light"
                  style={{ width: 110 }}
                  aria-hidden
                />
                <img
                  className="connect-entity-logo__dark"
                  src={LogoZohoDark}
                  alt=""
                  style={{ width: 165, height: 'auto' }}
                />
              </span>
            </EntityButton>
            <span
              style={{
                position: 'absolute',
                bottom: 10,
                left: 30,
                fontSize: 13,
                color: 'var(--color-on-logo-tile, #042a2a)',
                padding: '2px 4px',
              }}
            >
              {t('group.coming-soon')}
            </span>
          </Col>
        )}
      </Row>
      <Row
        justify={'space-between'}
        gutter={[0, 15]}
        style={{ marginTop: '20px' }}
      >
        <Col>
          <FeatureDotBadge dotId="connect-entity-excel">
            <EntityButton
              type="button"
              aria-label={t('organisation.connect-entity-excel-label')}
              onClick={() => onConnectExcel(Provider.EXCEL)}
              style={{
                flexDirection: 'column',
                justifyContent: 'flex-start',
                padding: '12px 10px 10px',
                gap: 8,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: 4,
                  width: '100%',
                  fontSize: 11,
                  lineHeight: 1.25,
                  color: 'var(--color-text-base, #e3eaeb)',
                  textAlign: 'start',
                  zIndex: 1,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  {t('organisation.connect-entity-excel-label')}
                </span>
                <Tooltip title={t('organisation.connect-entity-excel-help')}>
                  <span
                    role="img"
                    aria-label={t('organisation.connect-entity-excel-help')}
                    tabIndex={0}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    style={{ display: 'inline-flex', flexShrink: 0 }}
                  >
                    <QuestionCircleOutlined
                      style={{
                        fontSize: 15,
                        color: 'var(--color-text-muted, #a9b8b9)',
                        cursor: 'pointer',
                        marginTop: 1,
                      }}
                    />
                  </span>
                </Tooltip>
              </span>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  minHeight: 0,
                }}
              >
                <LogoExcel
                  style={{ width: 56, height: 'auto', flexShrink: 0 }}
                />
              </div>
            </EntityButton>
          </FeatureDotBadge>
        </Col>
        <Col style={{ position: 'relative' }}>
          <FeatureDotBadge dotId="connect-entity-receipts">
            <EntityButton
              type="button"
              className="connect-entity-receipts-tile"
              aria-label={t('organisation.connect-entity-receipts-label')}
              onClick={() => onConnectExcel(Provider.RECEIPTS)}
              style={{
                flexDirection: 'column',
                justifyContent: 'flex-start',
                padding: '12px 10px 10px',
                gap: 6,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: 4,
                  width: '100%',
                  fontSize: 11,
                  lineHeight: 1.25,
                  color: 'var(--color-text-base, #e3eaeb)',
                  textAlign: 'start',
                  zIndex: 1,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  {t('organisation.connect-entity-receipts-label')}
                </span>
                <Tooltip title={t('organisation.connect-entity-receipts-help')}>
                  <span
                    role="img"
                    aria-label={t('organisation.connect-entity-receipts-help')}
                    tabIndex={0}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    style={{ display: 'inline-flex', flexShrink: 0 }}
                  >
                    <QuestionCircleOutlined
                      style={{
                        fontSize: 15,
                        color: 'var(--color-text-muted, #a9b8b9)',
                        cursor: 'pointer',
                        marginTop: 1,
                      }}
                    />
                  </span>
                </Tooltip>
              </span>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  minHeight: 0,
                  gap: 4,
                }}
              >
                <AiScanningLogo size={56} />
                <Tooltip title={t('organisation.receipts-beta-tooltip')}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--color-primary, #41aaaf)',
                      backgroundColor: 'var(--color-layout-bg, #E9F6F7)',
                      padding: '2px 6px',
                      borderRadius: 4,
                      border: '1px solid var(--color-border, #dbeae3)',
                      cursor: 'help',
                    }}
                  >
                    <IconBetaStar
                      style={{ width: 14, height: 14, flexShrink: 0 }}
                    />
                    {t('common.beta')}
                  </span>
                </Tooltip>
              </div>
            </EntityButton>
          </FeatureDotBadge>
        </Col>
      </Row>
    </div>
  );
};
